import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { DataTable, FilterBar, UpdatedStamp } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

import { useControls, type Controls } from "./controls";
import { formatFigure } from "./format";
import {
  barWidth,
  BIGGEST_DROP_FLOOR,
  biggestDropLine,
  choicesLine,
  emptyLine,
  FLOOR_CAPTION,
  formatDropRate,
  formatSeconds,
  FUNNEL_SLUG,
  goalOptions,
  HEALTH_PROBLEM_GOAL,
  isBiggestDrop,
  kindOptions,
  noDoorLine,
  NOT_NAMED_TEXT,
  NOT_NAMED_TITLE,
  ONBOARDING_CAPTION,
  parseChoice,
  parseDoor,
  parseRange,
  RANGE_LABELS,
  RANGES,
  rangeWindow,
  stepTags,
  summaryLine,
  tableRows,
  unknownRoutesLine,
  visibleSteps,
  withCurrent,
  type Range,
  type TableRow,
} from "./onboarding-format";
import {
  DOORS,
  getFunnelOnboarding,
  statsMessageFor,
  statsOnboardingKey,
  type FunnelOnboardingStats,
  type OnboardingQuery,
  type OnboardingStep,
  type OnboardingTotals,
} from "./stats-api";

/** Before any good range has been read (a custom range with no start date on first load). */
const NO_QUERY: OnboardingQuery = { door: "all", product: "all", goal: "all", sub_goal: "all", from: "", to: "" };

// Precision Funnel C12c: the Onboarding tab (UX design 2026-10-03 §7, prototype v2 approved 8 Oct).
// Which AXIS onboarding screen loses the most people, by door, product, goal and which-kind choice.
// Every figure and the biggest drop are the funnel stats API's (I-4), real counts however small; this tab
// formats, orders and words. The funnel is fixed to precision-dosing: Door A people arrive from a
// pack QR, not a landing page.

/** UX §7.4 narrow screen: the table scrolls sideways inside its card while # and Step stay pinned. */
const PINNED_INDEX = "sticky left-0 z-10 bg-background";
const PINNED_STEP = "sticky left-12 z-10 bg-background";
const SCROLL_FRAME = "overflow-x-auto [&_table]:min-w-[40rem]";

type Choices = FunnelOnboardingStats["choices"];
const NO_CHOICES: Choices = { products: [], goals: [], sub_goals: {} };

const EMPTY_TOTALS: OnboardingTotals = {
  started: 0,
  finished: null,
  finish_rate: null,
  biggest_drop: null,
  signed_up_people: 0,
  not_signed_up_people: 0,
  no_door_people: 0,
};

/** URL keys this tab owns (through the shared `f_` filter codec). */
const DOOR = "door";
const PRODUCT = "product";
const GOAL = "goal";
const KIND = "kind";
const RANGE = "range";
const FROM = "from";
const TO = "to";

const DOOR_SPEC: FilterSpec = { name: DOOR, label: "Door", kind: "choice", options: DOORS.map((d) => ({ value: d.value, label: d.label })) };
const RANGE_SPEC: FilterSpec = { name: RANGE, label: "Range", kind: "choice", options: RANGES.map((r) => ({ value: r, label: RANGE_LABELS[r] })) };

/** Every group row reserves the chevron slot, so names line up whether or not the row can expand (AGENTS.md rule 13). */
function ToggleSpacer() {
  return <span aria-hidden="true" data-toggle-slot className="inline-block size-6 shrink-0" />;
}

function StepCell({ row, open, toggle, biggest }: { row: TableRow; open: boolean; toggle: () => void; biggest: boolean }) {
  const tags = stepTags(row);
  return (
    <span className={`flex items-center gap-2 ${row.nested ? "pl-5" : ""}`}>
      {row.is_group && row.memberCount > 0 ? (
        <Button
          data-nonmutating
          data-toggle-slot
          variant="ghost"
          size="icon-xs"
          aria-expanded={open}
          aria-label={`${open ? "Hide" : "Show"} the ${row.memberCount} ${row.label} ${row.memberCount === 1 ? "screen" : "screens"}`}
          onClick={toggle}
        >
          {open ? <ChevronDown /> : <ChevronRight />}
        </Button>
      ) : (
        <ToggleSpacer />
      )}
      <span className="text-ks-ink">{row.nested ? "↳ " : ""}{row.label}</span>
      {row.is_group ? (
        <span className="text-xs text-muted-foreground">
          {row.memberCount} {row.memberCount === 1 ? "screen" : "screens"}
        </span>
      ) : (
        <code className="text-xs text-muted-foreground">{row.route}</code>
      )}
      {tags.map((tag) => (
        <span key={tag} className="text-xs text-muted-foreground">
          {tag}
        </span>
      ))}
      {biggest && <span className="text-xs font-bold text-ks-purple">Biggest drop</span>}
    </span>
  );
}

/** Purple only where the service could name this row the biggest drop: a top-level row 30 or more people saw. */
function DropCell({ row, steps }: { row: TableRow; steps: OnboardingStep[] }) {
  const text = formatDropRate(row.drop_rate);
  const width = barWidth(row, steps);
  const eligible = !row.nested && (row.viewed ?? 0) >= BIGGEST_DROP_FLOOR;
  return (
    <span className="flex items-center justify-end gap-2">
      <span>{text}</span>
      <span aria-hidden="true" className="inline-block h-1.5 w-16 shrink-0">
        {width > 0 && <span className={`block h-full ${eligible ? "bg-ks-purple" : "bg-ks-grey-2"}`} style={{ width: `${width}%` }} />}
      </span>
    </span>
  );
}

function columns(steps: OnboardingStep[], totals: OnboardingTotals, open: Set<string>, toggle: (key: string) => void): ColumnDef<TableRow, unknown>[] {
  return [
    { id: "index", header: "#", accessorFn: (row) => (row.index === null ? "" : String(row.index)), meta: { align: "right", width: "3rem", className: PINNED_INDEX } },
    {
      id: "step",
      header: "Step",
      accessorFn: (row) => row.label,
      cell: ({ row }) => (
        <StepCell
          row={row.original}
          open={row.original.group_key !== null && open.has(row.original.group_key)}
          toggle={() => row.original.group_key && toggle(row.original.group_key)}
          biggest={isBiggestDrop(row.original, totals)}
        />
      ),
      meta: { width: "45%", className: PINNED_STEP },
    },
    { id: "viewed", header: "Viewed", accessorFn: (row) => formatFigure(row.viewed), meta: { align: "right", width: "10%" } },
    { id: "completed", header: "Completed", accessorFn: (row) => formatFigure(row.completed), meta: { align: "right", width: "12%" } },
    {
      id: "drop",
      header: "Drop %",
      accessorFn: (row) => formatDropRate(row.drop_rate),
      cell: ({ row }) => <DropCell row={row.original} steps={steps} />,
      meta: { align: "right", width: "16%", sortable: true },
    },
    { id: "time", header: "Median time", accessorFn: (row) => formatSeconds(row.median_seconds), meta: { align: "right", width: "12%" } },
  ];
}

function OnboardingControls({
  controls,
  choices,
  served,
  range,
  rangeError,
}: {
  controls: Controls;
  choices: Choices;
  served: boolean;
  range: Range;
  rangeError: string | null;
}) {
  // A value the reader set stays an active pill even when this window's choices do not list it.
  const products = withCurrent(choices.products, controls.values[PRODUCT]);
  const goals = withCurrent(goalOptions(choices), controls.values[GOAL]);
  const goal = parseChoice(controls.values[GOAL]);
  const productSpec: FilterSpec = {
    name: PRODUCT,
    label: "Product",
    kind: "choice",
    anyLabel: "All products",
    options: products.map((value) => ({ value, label: value })),
    disabled: products.length === 0,
  };
  const goalSpec: FilterSpec = { name: GOAL, label: "Goal", kind: "choice", anyLabel: "All goals", options: goals.map((value) => ({ value, label: value })) };
  const kinds = kindOptions(goal, choices.sub_goals);
  const kindValues = kinds.length === 0 ? [] : withCurrent(kinds.map((k) => k.value), controls.values[KIND]);
  const kindSpec: FilterSpec = { name: KIND, label: "Which kind", kind: "choice", anyLabel: "All", options: kindValues.map((value) => ({ value, label: value })), disabled: kindValues.length === 0 };
  const kindHelper = goal === "all" ? "Pick a goal first." : goal === HEALTH_PROBLEM_GOAL ? "Health problems are not offered as a filter." : null;
  const set = (name: string, value: string | null) => controls.onChange({ ...controls.values, [name]: value });
  return (
    <div className="flex flex-col gap-3">
      <FilterBar
        layout="segmented"
        filters={[DOOR_SPEC, RANGE_SPEC]}
        values={{ ...controls.values, [DOOR]: parseDoor(controls.values[DOOR]), [RANGE]: range }}
        onChange={controls.onChange}
      />
      <FilterBar
        layout="inline"
        filters={[productSpec, goalSpec, kindSpec]}
        values={controls.values}
        onChange={(next) => controls.onChange(next[GOAL] === controls.values[GOAL] ? next : { ...next, [KIND]: null })}
        trailing={
          <span className="flex flex-wrap gap-3">
            {served && choices.products.length === 0 && <span>Product is not recorded yet.</span>}
            {kindHelper && <span>{kindHelper}</span>}
          </span>
        }
      />
      {range === "custom" && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            From
            <Input data-nonmutating type="date" value={String(controls.values[FROM] ?? "")} onChange={(e) => set(FROM, e.target.value || null)} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            To
            <Input data-nonmutating type="date" value={String(controls.values[TO] ?? "")} onChange={(e) => set(TO, e.target.value || null)} />
          </label>
          {rangeError && (
            <span role="alert" className="text-sm text-ks-warn">
              {rangeError}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function OnboardingTab({ areaName }: { areaName: string }) {
  const controls = useControls();
  // The last served choices, for the pickers. The request carries the URL's own values, so a shared
  // link filters from its first fetch and a filter is never dropped because a window has no sign-ups.
  const [choices, setChoices] = useState<Choices>(NO_CHOICES);
  const [served, setServed] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<"asc" | "desc" | null>(null);
  const range = parseRange(controls.values[RANGE]);
  const window = rangeWindow(range, new Date(), { from: controls.values[FROM], to: controls.values[TO] });
  const rangeError = "error" in window ? window.error : null;
  const goal = parseChoice(controls.values[GOAL]);
  // A bad custom range (UX §7.3) keeps the last good request on screen beside its error.
  const lastGood = useRef<OnboardingQuery | null>(null);
  if ("from" in window) {
    lastGood.current = {
      door: parseDoor(controls.values[DOOR]),
      product: parseChoice(controls.values[PRODUCT]),
      goal,
      sub_goal: goal === "all" || goal === HEALTH_PROBLEM_GOAL ? "all" : parseChoice(controls.values[KIND]),
      from: window.from,
      to: window.to,
    };
  }
  const query = lastGood.current ?? NO_QUERY;
  // No placeholder data: a new filter shows the skeleton, never the previous request's figures under the new label.
  const onboarding = useQuery({
    queryKey: statsOnboardingKey(FUNNEL_SLUG, query),
    queryFn: () => getFunnelOnboarding(FUNNEL_SLUG, query),
    enabled: query !== NO_QUERY,
  });
  const stats = onboarding.data?.data;
  useEffect(() => {
    if (!stats) return;
    setChoices(stats.choices);
    setServed(true);
  }, [stats]);
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const visible = stats ? visibleSteps(stats.steps, query.goal) : [];
  const rows = tableRows(visible, open, sort);
  const empty = stats ? emptyLine(stats) : null;
  const footer = stats ? [choicesLine(stats.totals, stats.choices_as_of), noDoorLine(stats.totals), unknownRoutesLine(stats)].filter(Boolean) : [];

  return (
    <div className="flex flex-col gap-4">
      <OnboardingControls controls={controls} choices={choices} served={served} range={range} rangeError={rangeError} />
      {onboarding.isError ? (
        <Alert>
          <AlertTitle>Onboarding figures unavailable</AlertTitle>
          <AlertDescription>
            {statsMessageFor(onboarding.error, areaName, "Onboarding figures")}{" "}
            <Button data-nonmutating variant="link" className="px-0" onClick={() => void onboarding.refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {stats ? (
            <div className="flex flex-col gap-1">
              <p className="text-base text-ks-ink" aria-live="polite">
                {summaryLine(stats, range)}
              </p>
              {biggestDropLine(stats.totals) && <p className="text-base font-bold text-ks-ink">{biggestDropLine(stats.totals)}</p>}
              <UpdatedStamp generatedAt={stats.data_as_of} />
            </div>
          ) : (
            onboarding.isLoading && <Skeleton className="h-12 w-full" />
          )}
          {stats && !stats.screens_named && (
            <Alert>
              <AlertTitle>{NOT_NAMED_TITLE}</AlertTitle>
              <AlertDescription>{NOT_NAMED_TEXT}</AlertDescription>
            </Alert>
          )}
          {empty ? (
            <p className="text-sm text-ks-ink">
              {empty}{" "}
              {stats && stats.totals.started === 0 && (query.door !== "all" || query.product !== "all" || query.goal !== "all" || query.sub_goal !== "all") && (
                <Button
                  data-nonmutating
                  variant="link"
                  className="px-0"
                  onClick={() => controls.onChange({ ...controls.values, [DOOR]: null, [PRODUCT]: null, [GOAL]: null, [KIND]: null })}
                >
                  Clear filters
                </Button>
              )}
            </p>
          ) : (
            <div className={SCROLL_FRAME}>
              <DataTable
                columns={columns(visible, stats?.totals ?? EMPTY_TOTALS, open, toggle)}
                rows={rows}
                pending={onboarding.isLoading}
                density="compact"
                page={{ pageIndex: 0, pageSize: Math.max(rows.length, 1), rowCount: rows.length }}
                onPageChange={() => {}}
                sort={sort ? { id: "drop", direction: sort } : undefined}
                onSort={() => setSort((s) => (s === "desc" ? "asc" : s === "asc" ? null : "desc"))}
                emptyMessage="No onboarding screens to show."
                caption="Onboarding screens in flow order with people viewed, completed, drop % and median time"
              />
            </div>
          )}
          <p className="text-sm text-muted-foreground">{ONBOARDING_CAPTION}</p>
          <p className="text-sm text-muted-foreground">{FLOOR_CAPTION}</p>
          {footer.map((line) => (
            <p key={line as string} className="text-sm text-muted-foreground">
              {line}
            </p>
          ))}
        </>
      )}
    </div>
  );
}
